import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Search, ChevronDown, Package, Truck, CheckCircle2, Ban, RotateCcw, Clock, User, Copy, Check,
} from "lucide-react";
import type { AppDispatch, RootState } from "../../redux/store";
import { cancelOrder, refundOrder, markOrderDelivered, Order } from "../../redux/ordersSlice";

/* Seller view of orders: one list, filters, search, ship / cancel / refund.
   Data comes only from the Redux slices (no direct Firebase calls here):
   - ordersSlice  -> hostOrders (orders that contain this seller's products)
   - productSlice -> ownerProducts (this seller's products; gives the seller uid,
                     the set of own productIds and a photo fallback) */

/* ═══════════════════════════ STYLES: white, black and red (scoped to .oo) ═══════════════════════════ */
const INK = "#000";
const BODY = "#5C5C5C";
const LINE = "#E5E5E5";
const RED = "#E10000";
const RED_DARK = "#B30000";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Jost:wght@300;400;500;600&display=swap');
.oo, .oo *, .oo *::before, .oo *::after{ box-sizing:border-box; font-family:'Jost',system-ui,sans-serif; }
.oo{ display:flex; flex-direction:column; gap:14px; width:100%; color:${INK}; background:#fff; }
.oo p{ margin:0; }
.oo button{ font-family:inherit; cursor:pointer; border:none; background:none; color:inherit; }
.oo button:disabled{ cursor:not-allowed; opacity:.5; }
.oo button:focus-visible, .oo input:focus-visible{ outline:2px solid ${RED}; outline-offset:2px; }

/* shared look */
.oo .af-glass{ background:#fff; border:1px solid ${LINE}; border-radius:18px; }
.oo .af-card{ padding:0; }
.oo .af-muted{ color:${BODY}; }
.oo .af-error{ background:#fff; color:${RED}; border:1px solid ${RED}; border-radius:12px; padding:11px 14px; font-size:13px; font-weight:500; letter-spacing:.04em; }
.oo .af-btn{ display:inline-flex; align-items:center; justify-content:center; gap:8px; height:46px; padding:0 26px; border-radius:999px;
  background:${INK}; border:1px solid ${INK}; color:#fff !important; font-size:12px; font-weight:500; letter-spacing:.22em; text-transform:uppercase;
  transition:background .25s, border-color .25s, color .25s, transform .25s; }
.oo .af-btn:hover:not(:disabled){ background:${RED}; border-color:${RED}; transform:translateY(-1px); }
.oo .af-btn:active:not(:disabled){ background:${RED_DARK}; border-color:${RED_DARK}; transform:translateY(0); }
.oo .af-btn-sm{ height:38px; padding:0 18px; font-size:11px; }
.oo .af-btn-ghost{ background:#fff; color:${INK} !important; border:1px solid ${INK}; }
.oo .af-btn-ghost:hover:not(:disabled){ background:#fff; color:${RED} !important; border-color:${RED}; }
.oo .af-btn-ghost:active:not(:disabled){ background:#fff; color:${RED_DARK} !important; border-color:${RED_DARK}; }

/* search + filter pills */
.oo-search{ display:flex; align-items:center; gap:10px; padding:0 18px; height:50px; border-radius:999px; color:${BODY};
  background:#fff; border:1px solid #D4D4D4; transition:border-color .2s, box-shadow .2s; }
.oo-search:hover{ border-color:${INK}; }
.oo-search:focus-within{ border-color:${INK}; box-shadow:0 0 0 3px rgba(0,0,0,.08); }
.oo-search input{ flex:1; min-width:0; border:none; outline:none; background:transparent; font-size:15px; font-weight:400; letter-spacing:.04em; color:${INK}; height:100%; }
.oo-search input::placeholder{ color:#A3A3A3; }
.oo-pills{ display:flex; gap:8px; overflow-x:auto; padding:2px 2px 6px; scrollbar-width:none; -webkit-overflow-scrolling:touch; }
.oo-pills::-webkit-scrollbar{ display:none; }
.oo-pill{ flex-shrink:0; display:inline-flex; align-items:center; gap:8px; height:40px; padding:0 18px; border-radius:999px; white-space:nowrap;
  background:#fff; border:1px solid ${INK} !important; font-size:11px; font-weight:500; letter-spacing:.18em; text-transform:uppercase; color:${INK}; transition:all .2s; }
.oo-pill:hover{ border-color:${RED} !important; color:${RED}; }
.oo-pill.on{ background:${INK}; color:#fff; }
.oo-pill.on:hover{ background:${RED}; border-color:${RED} !important; color:#fff; }
.oo-n{ min-width:22px; height:20px; padding:0 6px; border-radius:999px; display:inline-flex; align-items:center; justify-content:center;
  font-size:11px; font-weight:500; letter-spacing:0; background:#F0F0F0; color:${INK}; }
.oo-pill.on .oo-n{ background:#fff; color:${INK}; }

/* empty state */
.oo-empty{ display:flex; flex-direction:column; align-items:center; gap:8px; text-align:center; padding:44px 24px !important; color:${INK}; }
.oo-empty b{ font-family:'Cormorant Garamond',serif; font-size:28px; font-weight:500; color:${INK}; }
.oo-empty span{ font-size:14px; max-width:300px; line-height:22px; }

/* order card */
.oo-card{ overflow:hidden; transition:border-color .2s, transform .2s; }
.oo-card:hover{ border-color:${INK}; transform:translateY(-1px); }
.oo-card.open{ border-color:${INK}; }
.oo-head{ display:flex; align-items:center; gap:14px; width:100%; padding:14px 16px; text-align:left; }
.oo-thumb{ width:60px; height:60px; border-radius:14px; overflow:hidden; flex-shrink:0; display:flex; align-items:center; justify-content:center;
  background:#F4F4F4; border:1px solid ${LINE}; color:#9A9A9A; }
.oo-thumb img{ width:100%; height:100%; object-fit:cover; display:block; }
.oo-main{ flex:1; min-width:0; display:flex; flex-direction:column; gap:3px; }
.oo-title{ font-size:16px; font-weight:500; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.oo-title em{ font-style:normal; font-size:10px; font-weight:500; letter-spacing:.14em; text-transform:uppercase; color:${RED}; margin-left:8px; }
.oo-sub{ display:flex; align-items:center; gap:5px; font-size:12.5px; color:${BODY}; font-weight:400; letter-spacing:.03em; flex-wrap:wrap; }
.oo-side{ display:flex; flex-direction:column; align-items:flex-end; gap:6px; flex-shrink:0; }
.oo-side > b{ font-family:'Cormorant Garamond',serif; font-size:24px; font-weight:500; line-height:1; }
.oo-chev{ color:${BODY}; transition:transform .2s, color .2s; }
.oo-head:hover .oo-chev{ color:${RED}; }
.oo-card.open .oo-chev{ transform:rotate(180deg); }

/* status badges */
.oo-badge{ display:inline-flex; align-items:center; gap:5px; font-size:10px; font-weight:500; letter-spacing:.14em; text-transform:uppercase; padding:4px 10px; border-radius:999px; white-space:nowrap; border:1px solid transparent; }
.oo-pending{ background:#fff; border-color:#D4D4D4; color:${BODY}; }
.oo-toship{ background:${RED}; border-color:${RED}; color:#fff; }
.oo-shipped{ background:${INK}; border-color:${INK}; color:#fff; }
.oo-delivered{ background:#fff; border-color:${INK}; color:${INK}; }
.oo-cancelled{ background:#F4F4F4; border-color:#F4F4F4; color:#8A8A8A; }
.oo-refunded{ background:#fff; border-color:${RED}; color:${RED}; }

/* expanded body */
.oo-body{ padding:4px 16px 16px; border-top:1px solid ${LINE}; display:flex; flex-direction:column; gap:12px; animation:ooOpen .2s ease-out; }
@keyframes ooOpen{ from{ opacity:0; transform:translateY(-6px); } to{ opacity:1; transform:translateY(0); } }
.oo-items{ display:flex; flex-direction:column; gap:10px; padding-top:12px; }
.oo-item{ display:grid; grid-template-columns:48px 1fr auto; grid-template-areas:"photo name total" "photo qty total"; column-gap:12px; row-gap:1px; align-items:center;
  padding:8px; border-radius:14px; background:#fff; border:1px solid ${LINE}; }
.oo-iphoto{ grid-area:photo; width:48px; height:48px; border-radius:12px; overflow:hidden; display:flex; align-items:center; justify-content:center;
  background:#F4F4F4; color:#9A9A9A; }
.oo-iphoto img{ width:100%; height:100%; object-fit:cover; display:block; }
.oo-iname{ grid-area:name; font-size:14.5px; font-weight:500; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.oo-item > .af-muted{ grid-area:qty; font-size:12.5px; }
.oo-item > b{ grid-area:total; font-family:'Cormorant Garamond',serif; font-size:20px; font-weight:500; }
.oo-sum{ display:flex; align-items:center; justify-content:space-between; padding:14px 16px; border-radius:14px; background:#F7F7F7; border:1px solid ${LINE};
  font-size:11.5px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; }
.oo-sum b{ font-family:'Cormorant Garamond',serif; font-size:26px; font-weight:500; letter-spacing:0; text-transform:none; color:${INK}; }
.oo-actions{ display:flex; flex-wrap:wrap; align-items:center; gap:8px; }
.oo-note{ display:inline-flex; align-items:center; gap:6px; font-size:12px; font-weight:500; letter-spacing:.1em; text-transform:uppercase; color:${INK}; }

@media (max-width:480px){
  .oo-head{ padding:12px; gap:10px; }
  .oo-thumb{ width:52px; height:52px; border-radius:12px; }
  .oo-title{ font-size:15px; }
  .oo-body{ padding:4px 12px 14px; }
}
@media (prefers-reduced-motion: reduce){ .oo *{ transition:none !important; animation:none !important; } }
`;

type SK = "Pending" | "To ship" | "Shipped" | "Delivered" | "Cancelled" | "Refunded";
type Scoped = Order & { mine: Order["items"]; mineTotal: number; mineQty: number };

const STATUS_ICON: Record<SK, React.ReactNode> = {
  Pending: <Clock size={12} />,
  "To ship": <Package size={12} />,
  Shipped: <Truck size={12} />,
  Delivered: <CheckCircle2 size={12} />,
  Cancelled: <Ban size={12} />,
  Refunded: <RotateCcw size={12} />,
};

const statusOf = (o: Order): SK => {
  if (o.isRefunded) return "Refunded";
  if (o.isCancelled) return "Cancelled";
  if (o.isDelivered || o.deliveryStatus?.buyerReceived) return "Delivered";
  if (o.deliveryStatus?.sellerDelivered || o.status === "shipped") return "Shipped";
  if (o.isPaid) return "To ship";
  return "Pending";
};

const money = (n?: number) => (!n || isNaN(n) ? "0.00" : Number(n).toFixed(2));
const when = (ms?: number | null) =>
  ms ? new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "-";

type Filter = "active" | "Shipped" | "Delivered" | "Cancelled" | "all";

export default function OwnerOrders() {
  const dispatch = useDispatch<AppDispatch>();
  const { hostOrders, actionLoading, error } = useSelector((s: RootState) => s.orders);
  const ownerProducts = useSelector((s: RootState) => s.products.ownerProducts);

  const [filter, setFilter] = useState<Filter>("active");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [copied, setCopied] = useState("");

  /* seller uid, own product ids and product photos, all taken from the product slice */
  const uid = ownerProducts[0]?.ownerId || "";
  const myProductIds = useMemo(() => new Set(ownerProducts.map((p) => p.productId)), [ownerProducts]);
  const photoByProduct = useMemo(() => {
    const m = new Map<string, string>();
    ownerProducts.forEach((p) => { if (p.images?.[0]) m.set(p.productId, p.images[0]); });
    return m;
  }, [ownerProducts]);

  const orders = useMemo<Scoped[]>(
    () =>
      hostOrders.map((o) => {
        const mine = o.items
          .filter((i) => {
            if ((o.ownerIds?.length ?? 0) <= 1) return true;      // single-seller order: every line is mine
            if (i.ownerId && uid) return i.ownerId === uid;       // multi-seller: match by ownerId
            return myProductIds.has(i.productId);                 // fallback: match by my products
          })
          .map((i) => ({ ...i, photo: i.photo || photoByProduct.get(i.productId) }));
        return {
          ...o,
          mine,
          mineTotal: mine.reduce((s, i) => s + i.price * i.quantity, 0),
          mineQty: mine.reduce((s, i) => s + i.quantity, 0),
        };
      }),
    [hostOrders, uid, myProductIds, photoByProduct]
  );

  const count = (f: Filter) =>
    orders.filter((o) => {
      const s = statusOf(o);
      if (f === "all") return true;
      if (f === "active") return s === "Pending" || s === "To ship";
      if (f === "Cancelled") return s === "Cancelled" || s === "Refunded";
      return s === f;
    }).length;

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      const s = statusOf(o);
      if (filter === "active" && !(s === "Pending" || s === "To ship")) return false;
      if (filter === "Shipped" && s !== "Shipped") return false;
      if (filter === "Delivered" && s !== "Delivered") return false;
      if (filter === "Cancelled" && s !== "Cancelled" && s !== "Refunded") return false;
      if (!q) return true;
      return `${o.orderId} ${o.customerId} ${o.mine.map((i) => i.name).join(" ")}`.toLowerCase().includes(q);
    });
  }, [orders, filter, search]);

  const ship = (o: Order) => dispatch(markOrderDelivered({ customerId: o.customerId, orderId: o.orderId, role: "owner" }));
  const cancel = (o: Order) => {
    if (window.confirm("Cancel this order?")) dispatch(cancelOrder({ customerId: o.customerId, orderId: o.orderId }));
  };
  const refund = (o: Order) => {
    if (window.confirm("Mark this order as refunded? Send the money back from your payment dashboard too."))
      dispatch(refundOrder({ customerId: o.customerId, orderId: o.orderId }));
  };
  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(text); setTimeout(() => setCopied(""), 1200); } catch { /* blocked */ }
  };

  const pills: { id: Filter; label: string; icon: React.ReactNode }[] = [
    { id: "active", label: "To do", icon: <Package size={13} /> },
    { id: "Shipped", label: "Shipped", icon: <Truck size={13} /> },
    { id: "Delivered", label: "Delivered", icon: <CheckCircle2 size={13} /> },
    { id: "Cancelled", label: "Cancelled", icon: <Ban size={13} /> },
    { id: "all", label: "All", icon: <Clock size={13} /> },
  ];

  return (
    <div className="oo">
      <style>{CSS}</style>

      <label className="oo-search">
        <Search size={16} />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product, buyer, order no." />
      </label>

      <div className="oo-pills">
        {pills.map((p) => (
          <button key={p.id} type="button" className={`oo-pill${filter === p.id ? " on" : ""}`} onClick={() => setFilter(p.id)}>
            {p.icon} {p.label} <span className="oo-n">{count(p.id)}</span>
          </button>
        ))}
      </div>

      {error && <p className="af-error">{error}</p>}

      {shown.length === 0 && (
        <div className="af-glass af-card oo-empty">
          <Package size={34} strokeWidth={1.4} />
          <b>{orders.length === 0 ? "No orders yet" : "No orders match"}</b>
          <span className="af-muted">
            {orders.length === 0 ? "When someone buys from your shop, the order lands here." : "Clear the search or pick another filter."}
          </span>
        </div>
      )}

      {shown.map((o) => {
        const s = statusOf(o);
        const key = `${o.customerId}-${o.orderId}`;
        const open = openId === key;
        const first = o.mine[0];
        const live = s === "Pending" || s === "To ship";
        const shipped = s === "Shipped";
        const ref = (o.paymentId || o.orderId).slice(0, 12).toUpperCase();
        return (
          <div key={key} className={`af-glass af-card oo-card${open ? " open" : ""}`}>
            <button type="button" className="oo-head" onClick={() => setOpenId(open ? null : key)} aria-expanded={open}>
              <span className="oo-thumb">
                {first?.photo ? <img src={first.photo} alt="" /> : <Package size={22} strokeWidth={1.5} />}
              </span>
              <span className="oo-main">
                <b className="oo-title">
                  {first?.name || "Order"}
                  {o.mine.length > 1 && <em>+{o.mine.length - 1} more</em>}
                </b>
                <span className="oo-sub">#{ref} · {when(o.createdAt)} · {o.mineQty} item{o.mineQty === 1 ? "" : "s"}</span>
                <span className="oo-sub"><User size={12} /> {o.customerId.slice(0, 8)}</span>
              </span>
              <span className="oo-side">
                <span className={`oo-badge oo-${s.replace(" ", "").toLowerCase()}`}>{STATUS_ICON[s]} {s}</span>
                <b>€{money(o.mineTotal)}</b>
                <ChevronDown size={16} className="oo-chev" />
              </span>
            </button>

            {open && (
              <div className="oo-body">
                <div className="oo-items">
                  {o.mine.map((i, n) => (
                    <div key={i.productId + n} className="oo-item">
                      <span className="oo-iphoto">{i.photo ? <img src={i.photo} alt="" /> : <Package size={18} strokeWidth={1.5} />}</span>
                      <span className="oo-iname">{i.name}</span>
                      <span className="af-muted">{i.quantity} × €{money(i.price)}</span>
                      <b>€{money(i.price * i.quantity)}</b>
                    </div>
                  ))}
                </div>

                <div className="oo-sum">
                  <span>You keep</span>
                  <b>€{money(o.mineTotal)}</b>
                </div>
                {o.paymentProvider && <p className="oo-sub">Paid with {o.paymentProvider}{o.currency ? ` (${String(o.currency).toUpperCase()})` : ""}</p>}
                {o.cancelReason && <p className="oo-sub">Reason: {o.cancelReason}</p>}

                <div className="oo-actions">
                  {live && o.isPaid && (
                    <button type="button" className="af-btn af-btn-sm" disabled={actionLoading} onClick={() => ship(o)}>
                      <Truck size={15} /> Mark shipped
                    </button>
                  )}
                  {shipped && <span className="oo-note"><CheckCircle2 size={14} /> Shipped, waiting for the buyer</span>}
                  {live && (
                    <button type="button" className="af-btn af-btn-sm af-btn-ghost" disabled={actionLoading} onClick={() => cancel(o)}>
                      <Ban size={15} /> Cancel
                    </button>
                  )}
                  {o.isPaid && !o.isRefunded && (
                    <button type="button" className="af-btn af-btn-sm af-btn-ghost" disabled={actionLoading} onClick={() => refund(o)}>
                      <RotateCcw size={15} /> Refund
                    </button>
                  )}
                  <button type="button" className="af-btn af-btn-sm af-btn-ghost" onClick={() => copy(o.orderId)}>
                    {copied === o.orderId ? <Check size={15} /> : <Copy size={15} />} Order ID
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}